import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface RankUpModalProps {
  oldRank: number;
  newRank: number;
  onClose: () => void;
}

/**
 * RankUpModal - SavWar'da rütbe yükseldiğinde gösterilen kutlama modali
 * 
 * Kullanım:
 *   <RankUpModal 
 *     oldRank={5} 
 *     newRank={6} 
 *     onClose={() => setShowModal(false)}
 *   />
 */
export const RankUpModal: React.FC<RankUpModalProps> = ({ oldRank, newRank, onClose }) => {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Auto-close after 5 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    // Stop confetti after 3 seconds
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, [onClose]);

  // Rank emoji mapping (1-23)
  const getRankEmoji = (rank: number): string => {
    const emojis = [
      "🪖", "⚔️", "🛡️", "🏹", "🗡️", "🎯", "👑", "🔱", "⚡",
      "🌟", "💎", "🏆", "👊", "🦅", "🐉", "🔥", "💪", "🌠",
      "🎖️", "🏅", "👹", "⚔️", "🌌",
    ];
    return emojis[rank - 1] || "🎖️";
  };

  const oldRankEmoji = getRankEmoji(oldRank);
  const newRankEmoji = getRankEmoji(newRank);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.85)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={onClose}
    >
      {/* Confetti particles */}
      {showConfetti && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: 50 }).map((_, i) => {
            const colors = ["#FFD700", "#FFA500", "#FF6B6B", "#4ECDC4", "#45B7D1", "#F7DC6F"];
            const color = colors[i % colors.length];
            const left = Math.random() * 100;
            const delay = Math.random() * 0.5;
            const duration = 2 + Math.random() * 1;
            const size = 8 + Math.random() * 8;

            return (
              <div
                key={i}
                className="animate-confetti"
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  top: "-50px",
                  width: `${size}px`,
                  height: `${size}px`,
                  background: color,
                  borderRadius: Math.random() > 0.5 ? "50%" : "2px",
                  animationDelay: `${delay}s`,
                  animationDuration: `${duration}s`,
                }}
              />
            );
          })}
        </div>
      )}

      {/* Modal content */}
      <div
        className="animate-slide-in-up"
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)",
          border: "3px solid #FFD700",
          borderRadius: "20px",
          padding: "40px 30px",
          maxWidth: "500px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 0 40px rgba(255, 215, 0, 0.4), 0 8px 32px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "rgba(255, 255, 255, 0.1)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#fff",
            fontSize: "20px",
            transition: "background 0.2s",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          }}
        >
          ×
        </button>

        {/* Title */}
        <div style={{ fontSize: "48px", marginBottom: "20px" }}>🎉</div>
        <h2
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            background: "linear-gradient(90deg, #FFD700, #FFA500, #FFD700)",
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "20px",
          }}
          className="animate-shimmer"
        >
          {t("effects.rankUp")}
        </h2>

        {/* Rank transition */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              opacity: 0.6,
              filter: "grayscale(50%)",
            }}
          >
            {oldRankEmoji}
          </div>
          <div style={{ fontSize: "32px", color: "#888" }}>→</div>
          <div
            className="animate-button-bounce"
            style={{
              fontSize: "80px",
              filter: "drop-shadow(0 0 20px #FFD700)",
            }}
          >
            {newRankEmoji}
          </div>
        </div>

        {/* Message */}
        <p
          style={{
            fontSize: "18px",
            color: "#ddd",
            marginBottom: "30px",
            lineHeight: 1.6,
          }}
        >
          {t("effects.rankUpMessage", { oldRank, newRank })}
        </p>

        {/* Continue button */}
        <button
          onClick={onClose}
          style={{
            background: "linear-gradient(135deg, #FFD700, #FFA500)",
            border: "none",
            borderRadius: "12px",
            padding: "14px 32px",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#000",
            cursor: "pointer",
            transition: "transform 0.2s, box-shadow 0.2s",
            boxShadow: "0 4px 12px rgba(255, 215, 0, 0.4)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 215, 0, 0.6)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 215, 0, 0.4)";
          }}
        >
          Harika! Devam Et
        </button>
      </div>
    </div>
  );
};
